import { useEffect, type FC } from "react";
import Modal from "@via/components/modal.tsx";
import { NOTIFICATION } from "@via/context/notifications-context.tsx";
import { useNotifications } from "@via/hooks/useNotifications.tsx";
import { useCreateAppRevision } from "@via/pages/apps/apps.api.tsx";
import {
  calculateVersion,
  checkIsSemanticVersion,
} from "@via/utils/helpers.tsx";
import { SemanticRevisionTypes, ModalSize } from "@via/utils/constants.tsx";
import { useForm, Controller } from "react-hook-form";
import FormTextAreaComponent from "@via/components/inputs/form-textarea-component.tsx";
import SelectComponent from "@via/components/select/select.tsx";
import { UpdateApplicationRequestBody } from "common";

interface ReviseAppModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  payload: UpdateApplicationRequestBody & { id: number };
  lastVersion: string;
  onSuccess: () => void;
}

const ReviseAppModal: FC<ReviseAppModalProps> = ({
  open,
  setOpen,
  payload,
  lastVersion,
  onSuccess,
}) => {
  const { setNotification } = useNotifications();
  const {
    mutateAsync: createAppRevision,
    isPending: isCreateAppRevisionLoading,
  } = useCreateAppRevision();

  const { control, handleSubmit, setValue, watch, reset } = useForm({
    defaultValues: {
      revisionType: SemanticRevisionTypes[0],
      revisedVersion: "1.0.0",
    },
  });

  const currentRevisionType = watch("revisionType");

  useEffect(() => {
    if (!lastVersion) return;
    const newVersion = calculateVersion(
      currentRevisionType?.value,
      lastVersion
    );
    setValue("revisedVersion", newVersion);
  }, [lastVersion, currentRevisionType, setValue]);

  // don't include permission, groupId in the submission because new revision will be private by default
  const handleCreateAppRevision = async data => {
    const { id, permission: _permission, groupId: _groupId, ...rest } = payload;
    for (const key in rest) {
      if (rest[key] === "" || rest[key] === null) {
        rest[key] = undefined;
      }
    }
    // null is a choice here ("Not set" or an empty input); undefined would make the revision inherit the source's value.
    for (const key of ["appKind", "appCpus", "appMemoryMb", "appWallMinutes"]) {
      if (payload[key] === null) {
        rest[key] = null;
      }
    }
    const submission = {
      ...rest,
      version: data.revisedVersion,
      revComment: data.revisedComment,
    };
    await createAppRevision({
      id,
      payload: submission,
    });
  };

  const handleRevisionTypeChange = selected => {
    const isValidVersion = checkIsSemanticVersion(lastVersion, true);
    if (!isValidVersion) {
      setNotification({
        id: "invalid-version-detected",
        message: "Invalid Version Detected",
        details: "Acceptable Versions Must Be Semantic (x.x.x) or (x.x)",
        variant: NOTIFICATION.ERROR,
      });
      return;
    }
    const newVersion = calculateVersion(selected.value, lastVersion);
    setValue("revisedVersion", newVersion);
    setValue("revisionType", selected);
  };

  const handleCancel = () => {
    setOpen(false);
    reset({
      revisionType: SemanticRevisionTypes[0],
      revisedVersion: "1.0.0",
    });
  };

  // Resolving true hands the close to Modal. A missing revision comment resolves
  // false and keeps the dialog open on its error, and a failed request rejects,
  // which also keeps it open. onSuccess moved here from a success effect, so it
  // runs only for the revision this confirm actually created.
  const handleConfirmModal = async (): Promise<boolean> => {
    let created = false;
    await handleSubmit(async data => {
      await handleCreateAppRevision(data);
      created = true;
    })();
    if (created) {
      onSuccess();
    }
    return created;
  };

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      title="Create App Revision"
      confirmText="Create Revision"
      size={ModalSize.Large}
      onCancel={handleCancel}
      isConfirmLoading={isCreateAppRevisionLoading}
      onConfirm={handleConfirmModal}>
      <form
        onSubmit={e => {
          e.preventDefault();
          e.stopPropagation();
        }}>
        <div className="mt-0">
          <span className="text-sm text-gray-600">
            The latest version of this app is{" "}
            <span className="font-medium">{lastVersion}</span> and the new
            version will be{" "}
            <span className="font-medium">{watch("revisedVersion")}</span>
          </span>

          <div className="my-6 flex w-full flex-row items-center">
            <label className="flex w-36 flex-row items-center justify-between text-sm font-medium leading-6 text-gray-900">
              Revision Type
            </label>
            <div className="w-[450px]">
              <Controller
                name="revisionType"
                control={control}
                render={({ field }) => (
                  <SelectComponent
                    {...field}
                    enableClear={false}
                    options={SemanticRevisionTypes}
                    onChange={selected => handleRevisionTypeChange(selected)}
                  />
                )}
              />
            </div>
          </div>

          <div className="my-6 flex w-full flex-row items-center">
            <label className="flex w-36 flex-row items-center justify-between text-sm font-medium leading-6 text-gray-900">
              Revision Comment
            </label>
            <div className="w-[450px]">
              <FormTextAreaComponent
                control={control}
                name="revisedComment"
                placeholder="Enter a Revision Comment"
                validations={{ required: "Revision comment is required" }}
              />
            </div>
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default ReviseAppModal;
